import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Users, Check, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { colorFor, initials } from "@/components/ui/avatar-initials";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLicense } from "@/hooks/useLicense";
import { useAuth } from "@/auth/AuthProvider";
import { api, ApiError } from "@/api/client";
import type { OrgInfo, UserOrgsResponse } from "@/api/types";
import { toast } from "sonner";

export function OrgSwitcher() {
  const { user, refreshAuth } = useAuth();
  const { license } = useLicense();
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [createError, setCreateError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchOrgs = useCallback(() => {
    if (!user) return;
    api<UserOrgsResponse>("/api/v2/user/orgs")
      .then((data) => setOrgs(data.orgs))
      .catch(() => setOrgs([]));
  }, [user]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  if (!user) return null;

  const handleSwitch = async (orgId: string, slug: string) => {
    if (switching) return;
    setSwitching(true);
    try {
      await api("/api/v2/auth/switch-org", {
        method: "POST",
        body: JSON.stringify({ org_id: orgId }),
      });
      await refreshAuth();
      window.location.href = `/${slug}`;
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        toast.error("Ya no eres miembro de esta organización");
      }
      setSwitching(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim() || newOrgName.length < 2) return;
    setCreateError("");
    setSwitching(true);
    try {
      const result = await api<{ org_id: string }>("/api/v2/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          email: user.email,
          org_name: newOrgName.trim(),
          name: user.name,
        }),
      });
      await api("/api/v2/auth/switch-org", {
        method: "POST",
        body: JSON.stringify({ org_id: result.org_id }),
      });
      await refreshAuth();
      window.location.href = "/";
    } catch {
      setCreateError("Failed to create organization");
      setSwitching(false);
    }
  };

  const hasMultipleOrgs = orgs.length > 1;

  return (
    <DropdownMenu open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchOrgs(); }}>
      <DropdownMenuTrigger
        disabled={switching}
        className="flex w-full items-center gap-2.5 rounded-xl bg-sidebar-foreground/5 px-2 py-2 text-left ring-1 ring-sidebar-border/40 transition-all hover:bg-sidebar-foreground/10 hover:ring-sidebar-border/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      >
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${colorFor(user.org_name)}`}>
          {initials(user.org_name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight text-sidebar-foreground">
            {user.org_name || "Organization"}
          </p>
          <p className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-muted-foreground">
            {license?.plan ?? "community"}
          </p>
        </div>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
        {/* Current org header */}
        <div className="px-3 py-3 flex items-center gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ${colorFor(user.org_name)}`}>
            {initials(user.org_name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{user.org_name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className="h-4 px-1.5 text-[10px] capitalize">
                {license?.plan ?? "community"}
              </Badge>
              <span className="flex items-center gap-1 text-xs text-emerald-500">
                <Users className="h-3 w-3" />
                {license?.seats_used ?? "—"} members
              </span>
            </div>
          </div>
        </div>
        {hasMultipleOrgs && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground px-3 py-1">
              Switch organization
            </DropdownMenuLabel>
            {orgs.map((org) => {
              const isCurrentOrg = org.slug === user.org_slug;
              return (
                <DropdownMenuItem
                  key={org.org_id}
                  disabled={switching || isCurrentOrg}
                  onSelect={() => {
                    if (!isCurrentOrg) handleSwitch(org.org_id, org.slug);
                  }}
                  className="flex items-center gap-3 px-3 py-2"
                >
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold ${colorFor(org.org_name)}`}>
                    {initials(org.org_name)}
                  </div>
                  <span className="flex-1 truncate text-sm">{org.org_name}</span>
                  {isCurrentOrg && <Check className="h-3.5 w-3.5 text-primary" />}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        <DropdownMenuSeparator />
        {!creating ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setCreating(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            className="flex items-center gap-2 px-3 text-muted-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Create new organization</span>
          </DropdownMenuItem>
        ) : (
          <div className="px-2 py-1.5 space-y-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Organization name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateOrg();
                if (e.key === "Escape") {
                  setCreating(false);
                  setNewOrgName("");
                  setCreateError("");
                }
              }}
              disabled={switching}
              className="w-full rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {createError && (
              <p className="text-xs text-destructive">{createError}</p>
            )}
            <div className="flex gap-1">
              <button
                onClick={handleCreateOrg}
                disabled={switching || newOrgName.trim().length < 2}
                className="flex-1 rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {switching ? "Creating..." : "Create"}
              </button>
              <button
                onClick={() => {
                  setCreating(false);
                  setNewOrgName("");
                  setCreateError("");
                }}
                disabled={switching}
                className="rounded-md border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
